import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import Spinner from './ui/Spinner';
import { InteractionStatus, useSafeMsal } from '../authRuntime';

/**
 * Protects routes — redirects to /login if not authenticated.
 * Critically: waits for MSAL to finish processing a redirect before
 * making any auth decision, preventing the SSO loop.
 */
function AuthGuard({ children, requiredRoles }) {
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();
  const { inProgress } = useSafeMsal();

  // While MSAL is processing a login/redirect, show a loading spinner.
  // Without this, AuthGuard incorrectly sees isAuthenticated=false and
  // redirects to /login before Microsoft's redirect is processed.
  if (inProgress !== InteractionStatus.None) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: 'var(--c-bg, #f8fafc)'
      }}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRoles && requiredRoles.length > 0) {
    const userRoles = user?.roles || [];
    const hasRole = userRoles.some((r) => requiredRoles.includes(r));
    if (!hasRole) {
      // Employees get sent to their portal; others to dashboard
      const isEmployee = userRoles.includes('Employee');
      return <Navigate to={isEmployee ? '/portal' : '/dashboard'} replace />;
    }
  }

  return children;
}

export default AuthGuard;
